import React, { useEffect, useRef } from 'react';
import {
    Slider,
    IconButton,
    Typography,
    Stack,
    FormControl,
    Select,
    MenuItem,
    SelectChangeEvent,
    SxProps,
    Theme
} from '@mui/material';
import {
    PlayArrow,
    Pause,
    SkipPrevious,
    SkipNext
} from '@mui/icons-material';
import { ChronologicalSliderProps } from './types/sunburst.types';

/**
 * ChronologicalSlider Component
 * 
 * Provides time-based navigation controls for the sunburst chart with:
 * - Year range slider
 * - Play/pause animation controls
 * - Playback speed adjustment
 * - Manual year selection
 */
export const ChronologicalSlider: React.FC<ChronologicalSliderProps & {
    sx?: SxProps<Theme>;
}> = ({
    yearRange,
    currentYear,
    isPlaying,
    playbackSpeed,
    onYearChange,
    onPlayToggle,
    onSpeedChange,
    className,
    sx
}) => {
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    // Handle automatic playback
    useEffect(() => {
        if (isPlaying) {
            intervalRef.current = setInterval(() => {
                const nextYear = currentYear >= yearRange.max ? yearRange.min : currentYear + 1;
                onYearChange(nextYear);
            }, 1000 / playbackSpeed); // Speed controls interval frequency
        } else {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [isPlaying, playbackSpeed, yearRange, currentYear, onYearChange]);

    const handleSliderChange = (event: Event, newValue: number | number[]): void => {
        const year = Array.isArray(newValue) ? newValue[0] : newValue;
        onYearChange(year);
    };

    const handleSpeedChange = (event: SelectChangeEvent<number>): void => {
        const speed = Number(event.target.value);
        onSpeedChange(speed);
    };

    const handlePreviousYear = (): void => {
        const newYear = Math.max(currentYear - 1, yearRange.min);
        onYearChange(newYear);
    };

    const handleNextYear = (): void => {
        const newYear = Math.min(currentYear + 1, yearRange.max);
        onYearChange(newYear);
    };

    const speedOptions = [
        { value: 0.5, label: '0.5x' },
        { value: 1, label: '1x' },
        { value: 2, label: '2x' },
        { value: 4, label: '4x' },
        { value: 8, label: '8x' }
    ];

    return (
        <Stack direction="row" flexWrap="nowrap" alignItems="center"  justifyItems="space-between"  spacing={0.5} 
        sx={{ ...sx }} >
            {/* Year Range Slider */}
                <Slider
                    value={currentYear}
                    min={yearRange.min}
                    max={yearRange.max}
                    step={1}
                    onChange={handleSliderChange}
                    valueLabelDisplay="auto"
                    marks={[
                        { value: yearRange.min, label: yearRange.min.toString() },
                        { value: yearRange.max, label: yearRange.max.toString() }
                    ]}
                sx={{
                        minWidth:'80%',
                        '& .MuiSlider-thumb': {
                            width: 16,
                            height: 16,
                        },
                        '& .MuiSlider-track': {
                            height: 4,
                        },
                        '& .MuiSlider-rail': {
                            height: 4,
                        }
                    }}
                />



            {/* Center: Controls and Year */}
            <FormControl size="small" sx={{ display: 'none', minWidth: 60 }}>
                <Select
                    value={playbackSpeed}
                    onChange={handleSpeedChange}
                    variant="outlined"
                    sx={{ fontSize: '0.875rem' }}
                >
                    {speedOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <Typography variant="body2" color="text.secondary" sx={{ display: 'none', minWidth: 60 }}>
                {isPlaying ? 'Playing' : 'Paused'}
            </Typography>
            <Stack direction="row" display="inline-flex" alignItems="center" sx={{ marginX:'2em', width:'100%'}} >
                        <IconButton 
                            onClick={handlePreviousYear}
                            disabled={currentYear <= yearRange.min}
                            size="small"
                        >
                            <SkipPrevious />
                        </IconButton>

                        <IconButton
                            onClick={onPlayToggle}
                            color="primary"
                            size="medium"
                        >
                            {isPlaying ? <Pause /> : <PlayArrow />}
                        </IconButton>

                        <IconButton
                            onClick={handleNextYear}
                            disabled={currentYear >= yearRange.max}
                            size="small"
                        >
                            <SkipNext />
                        </IconButton>
            </Stack>
           
            <Typography variant="caption" sx={{ fontSize:'1em', minWidth: 80, textAlign: 'center', fontWeight: 'bold' }}>
                {currentYear}
            </Typography>

        </Stack>
    );
};

export default ChronologicalSlider;